'use client';

import { Checkbox } from '@pickleballinc/react-ui';
import { ChangeEvent, useEffect } from 'react';

import { Dropdown } from '@/components/atoms';
import { PrintButton } from '@/components/atoms/PrintButton';
import { ViewOption } from '@/lib/types';
import { ScheduleMatchupPeriodsDto } from '@/infrastructure/network/dto/ScheduleMatchupPeriodsDto';
import { SchedulePeriodDropdown } from '@/components/molecules/SchedulePeriodDropdown';
import { SchedulerView } from '@/components/constants/enums';
import { useTeamLeagueStore } from '@/store/team-league-store';

interface SchedulerHeaderButtonsProps {
	handleSelectView: (value: string | number) => void;
	viewOptions: ViewOption[];
	isPendingScheduleView?: boolean;
	handleShowOnlyMyScheduler?: (value: boolean) => void;
	periods: ScheduleMatchupPeriodsDto[];
	setStartDate: (date: string) => void;
	setEndDate: (date: string) => void;
	selectedView: string;
}

export const SchedulerHeaderButtons = ({
	handleSelectView,
	viewOptions,
	isPendingScheduleView,
	handleShowOnlyMyScheduler,
	periods,
	setStartDate,
	setEndDate,
	selectedView
}: SchedulerHeaderButtonsProps) => {
	const teamLeagueStore = useTeamLeagueStore();

	useEffect(() => {
		if (!periods) return;

		if (periods.length > 0) {
			const firstPeriod = periods[0];

			setStartDate(firstPeriod?.startDate || '');
			setEndDate(firstPeriod?.endDate || '');
		}
	}, [selectedView]);

	const scheduleTitle = isPendingScheduleView ? 'Pending schedule' : 'Final Schedule';

	const handleViewChange = (value: string) => {
		handleSelectView(value);
	};

	return (
		<div className="print-hidden">
			<div>
				<p className="pb-6 text-2xl font-semibold">{scheduleTitle}</p>
			</div>
			<div className="flex flex-col gap-6 items-start sm:items-center sm:flex-row sm:justify-between">
				<div className="flex items-start  flex-col justify-between gap-4">
					{periods?.length > 0 && (
						<p>
							{teamLeagueStore.useScheduleGroups
								? 'Select event'
								: selectedView === SchedulerView.Week
									? 'Select week'
									: selectedView === SchedulerView.Schedule
										? 'Select month'
										: null}
						</p>
					)}

					<SchedulePeriodDropdown
						periods={periods}
						setStartDate={setStartDate}
						setEndDate={setEndDate}
						isMonthView={selectedView === SchedulerView.Schedule}
					/>
				</div>
				<div className="flex justify-between gap-2 rounded-xl sm:justify-normal">
					<div>
						<Dropdown
							options={viewOptions.map((option) => ({ value: option.value, label: option.label }))}
							placeholder="Select a view"
							onSelect={handleViewChange}
							showAll={false}
							value={selectedView}
						/>
					</div>
					<PrintButton />
				</div>
			</div>
			{!teamLeagueStore.useScheduleGroups && periods?.length > 0 && (
				<p className="pt-1">
					{selectedView === SchedulerView.Week
						? 'Only weeks with matchups scheduled will appear'
						: selectedView === SchedulerView.Schedule
							? 'Only months with matchups scheduled will appear'
							: null}
				</p>
			)}

			{isPendingScheduleView && (
				<p className="pt-6 text-sm">
					This schedule is pending - please approve the matches so the calendar can be finalized. If a date/time/location does not work,
					please propose new.
				</p>
			)}

			{isPendingScheduleView && handleShowOnlyMyScheduler && (
				<div className="flex pt-6">
					<Checkbox
						theme="orange"
						className="size-4 cursor-pointer"
						onChange={(e: ChangeEvent<HTMLInputElement>) => {
							handleShowOnlyMyScheduler(e.target.checked);
						}}
					/>
					<p className="pl-2 text-sm font-bold text-gray-700">Show only my schedule</p>
				</div>
			)}
		</div>
	);
};
